// The one rule for "what kind of chat is this", decided by the chat id — never
// by an engine flag (listener `isGroup` is only ever `true` for `@g.us`).
// WhatsApp jids encode the kind deterministically in their suffix.

export type ChatKind = 'group' | 'channel' | 'status' | 'broadcast' | 'direct';

/** `@broadcast` (`status@broadcast`, WhatsApp Status) and `@newsletter`
 *  (Channels): pseudo-chats nobody can answer. */
export const UNADDRESSABLE_JID_SUFFIXES: readonly string[] = ['@broadcast', '@newsletter'] as const;

/** Total: a non-string (a number from an imported sheet, an object, null) is `''`. */
const normalized = (chatId: unknown): string => (typeof chatId === 'string' ? chatId.trim().toLowerCase() : '');

/** `@g.us` → group, `@newsletter` → channel, exactly `status@broadcast` →
 *  status, any other `@broadcast` (a broadcast list) → broadcast, anything else
 *  (`@c.us`, `@lid`, …) → direct. */
export function chatKindOf(chatId: unknown): ChatKind {
    const id = normalized(chatId);
    if (id.endsWith('@g.us')) return 'group';
    if (id.endsWith('@newsletter')) return 'channel';
    if (id === 'status@broadcast') return 'status';
    if (id.endsWith('@broadcast')) return 'broadcast';
    return 'direct';
}

/** Can a reply ever be delivered to this chat — i.e. may an agent be woken by it? */
export function isAddressableChatId(chatId: unknown): boolean {
    const id = normalized(chatId);
    return id !== '' && !UNADDRESSABLE_JID_SUFFIXES.some((suffix) => id.endsWith(suffix));
}

/** A one-to-one conversation: addressable and not a group. */
export function isOneToOneChatId(chatId: unknown): boolean {
    return isAddressableChatId(chatId) && chatKindOf(chatId) !== 'group';
}

/** The chat an inbound listener message belongs to: the key's remote, else the sender. */
export function inboundChatId(msg: {
    id?: { remote?: { _serialized?: string } | null } | null;
    from?: { _serialized?: string; id?: { _serialized?: string } | null } | null;
} | null | undefined): string {
    const candidates = [
        msg?.id?.remote?._serialized,
        // @7.15 the `from.id` fallback goes with `from.id`.
        msg?.from?.id?._serialized,
        msg?.from?._serialized,
    ];
    return candidates.find((c): c is string => typeof c === 'string') ?? '';
}
